import { Skeleton } from "antd";
import { motion, useReducedMotion } from "framer-motion";
import parse from "html-react-parser";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toGetAboutUs } from "../../apis/actions/aboutUs.actions";
import { AppDispatch, RootState } from "../../apis/store";
import Care from "../../domains/pages/Sections/Care";

type AboutUsItem = {
  id: number;
  name?: string;
  summary?: string;
  details?: string;
  image_1920?: string | false | null;
};

const FALLBACK_IMAGES = [
  "assets/imgs/section-1/img-1.png",
  "assets/imgs/section-1/img-2.png",
  "assets/imgs/section-1/img-3.png",
];

const hasReadableHtml = (value?: string) =>
  Boolean(
    value
      ?.replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .trim()
  );

const itemImage = (item: AboutUsItem | undefined, fallback: string) =>
  typeof item?.image_1920 === "string" && item.image_1920.trim()
    ? item.image_1920
    : fallback;

const AboutCopy = ({ item }: { item?: AboutUsItem }) => {
  if (!item) return null;

  return (
    <>
      {item.summary?.trim() && (
        <p className="text-lg-medium neutral-500 ac-about-copy__summary">
          {item.summary}
        </p>
      )}
      {hasReadableHtml(item.details) && (
        <div className="text-md-medium neutral-500 ac-about-copy__details">
          {parse(item.details as string)}
        </div>
      )}
    </>
  );
};

const AboutContentSkeleton = () => (
  <div className="ac-about-content-skeleton" aria-hidden="true">
    <Skeleton.Input active block className="ac-about-content-skeleton__title" />
    <Skeleton.Input active size="small" block />
    <Skeleton.Input active size="small" block />
    <Skeleton.Input active size="small" block className="ac-about-content-skeleton__short" />
  </div>
);

const Section1 = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { aboutus } = useSelector((state: RootState) => state.about);
  const prefersReducedMotion = useReducedMotion();

  const status = aboutus?.status ?? "idle";
  const list = useMemo(
    () => (Array.isArray(aboutus?.list) ? aboutus.list : []) as AboutUsItem[],
    [aboutus?.list]
  );

  useEffect(() => {
    if (status !== "idle") return;
    dispatch(toGetAboutUs(i18n.language));
  }, [dispatch, i18n.language, status]);

  const findItem = (id: number, index: number) =>
    list.find((item) => item.id === id) ?? list[index];

  const introduction = findItem(1, 0);
  const mission = findItem(2, 1);
  const vision = findItem(3, 2);
  const story = findItem(4, 3);
  const isLoading = status === "idle" || status === "loading";
  const reveal = {
    hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 34 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  };
  const imageReveal = {
    hidden: prefersReducedMotion
      ? { opacity: 1 }
      : { opacity: 0, x: i18n.dir() === "rtl" ? -42 : 42, scale: 0.97 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { duration: prefersReducedMotion ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section className="section-1 background-body ac-about-page">
      <div className="container">
        <div className="ac-about-intro">
          <div className="row align-items-center g-4 g-lg-5">
            <div className="col-lg-5">
              <motion.div
                className="ac-about-intro__content ac--font-expo"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
              >
                {isLoading ? (
                  <AboutContentSkeleton />
                ) : (
                  <>
                    <span className="ac-about-eyebrow">{t("menu.aboutUs")}</span>
                    <h2 className="neutral-1000 ac-about-heading">
                      {introduction?.name ?? t("home.block1.title")}
                    </h2>
                    <AboutCopy
                      item={
                        introduction ?? {
                          id: 1,
                          summary: t("home.block1.description"),
                        }
                      }
                    />
                  </>
                )}
              </motion.div>
            </div>

            <div className="col-lg-7">
              <motion.div
                className="ac-about-collage"
                variants={imageReveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.18 }}
              >
                <img
                  className="ac-about-collage__main"
                  src={itemImage(introduction, FALLBACK_IMAGES[0])}
                  alt={introduction?.name ?? t("menu.aboutUs")}
                />
                <img
                  className="ac-about-collage__secondary"
                  src={itemImage(story, FALLBACK_IMAGES[1])}
                  alt=""
                  aria-hidden="true"
                />
                <motion.div
                  className="ac-about-collage__label ac--font-expo"
                  animate={prefersReducedMotion ? undefined : { y: [0, -7, 0] }}
                  transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
                >
                  <span>{t("home.block1.btn")}</span>
                  <strong>{t("home.block1.btn1")}</strong>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>

        {(isLoading || mission || vision) && (
          <div className="ac-about-pillars">
            <div className="ac-about-road" aria-hidden="true">
              <span />
            </div>
            <div className="row g-4">
              {[mission, vision].map((item, index) => (
                <div className="col-lg-6" key={item?.id ?? `pillar-${index}`}>
                  <motion.article
                    className={`ac-about-pillar ac-about-pillar--${index + 1}`}
                    variants={reveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ delay: prefersReducedMotion ? 0 : index * 0.12 }}
                  >
                    {isLoading ? (
                      <AboutContentSkeleton />
                    ) : (
                      <div className="ac--font-expo">
                        <span className="ac-about-pillar__mark" aria-hidden="true" />
                        <h3 className="neutral-1000 ac-about-pillar__title">
                          {item?.name}
                        </h3>
                        <AboutCopy item={item} />
                      </div>
                    )}
                  </motion.article>
                </div>
              ))}
            </div>
          </div>
        )}

        {story && !isLoading && (
          <motion.article
            className="ac-about-story"
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.16 }}
          >
            <div className="ac-about-story__media">
              <img
                className="ac-about-story__image"
                src={itemImage(story, FALLBACK_IMAGES[1])}
                alt={story.name ?? t("menu.aboutUs")}
              />
            </div>
            <div className="ac-about-story__content ac--font-expo">
              <span className="ac-about-eyebrow">{t("menu.aboutUs")}</span>
              <h2 className="neutral-1000 ac-about-heading">{story.name}</h2>
              <AboutCopy item={story} />
            </div>
          </motion.article>
        )}
      </div>

      <div className="ac-about-care">
        <Care />
      </div>
    </section>
  );
};

export default Section1;
